import LegalDocument, { type LegalSection } from "@/components/feature/LegalDocument";
import { SITE } from "@/config/site";

const sections: LegalSection[] = [
  {
    id: "acceptance",
    title: "1. Acceptance of terms",
    body: [
      `These Terms of Service ("Terms") govern your access to and use of ${SITE.name} and all related services, including browsing the directory, creating a listing, purchasing a paid plan and using the owner portal.`,
      "By accessing or using the service, you agree to be bound by these Terms and by our Privacy Policy. If you do not agree, you must not use the service.",
      "These Terms are an electronic record under the Information Technology Act, 2000 and the rules made thereunder, and do not require a physical or digital signature.",
    ],
  },
  {
    id: "eligibility",
    title: "2. Eligibility and accounts",
    body: [
      "You must be at least 18 years old and competent to contract under the Indian Contract Act, 1872 to create an account or list a business.",
      "You are responsible for the accuracy of the information on your account, for keeping your login credentials confidential, and for all activity that occurs under your account.",
    ],
  },
  {
    id: "listings",
    title: "3. Business listings",
    body: [
      "Owners are responsible for the accuracy of the information they submit, including business name, address, contact details, opening hours, categories and media.",
      "We reserve the right to review, edit, reject or remove any listing that is inaccurate, misleading, duplicated, unlawful or that otherwise violates these Terms.",
      "A free listing is provided as-is. Paid plans add features such as verification, priority placement and analytics, as described on the Listing Plans page.",
    ],
  },
  {
    id: "verification",
    title: "4. Verification badges",
    body: [
      "A verified badge indicates that we performed a reasonable check of a business's address and ownership details at the time of verification.",
      "Verification is not a guarantee of quality, safety or continued accuracy. Details can change and should be confirmed directly with the business before relying on them.",
    ],
  },
  {
    id: "reviews",
    title: "5. Reviews and user content",
    body: [
      "You agree not to post content that is false, defamatory, abusive, obscene, discriminatory, spam or otherwise unlawful, including content that infringes the intellectual property of others.",
      "We may moderate, remove or refuse to publish any content at our discretion, and may suspend accounts that repeatedly breach these Terms.",
      "Business owners may respond to genuine reviews but must not post fake reviews, offer incentives for positive reviews, or manipulate ratings.",
    ],
  },
  {
    id: "payments",
    title: "6. Plans and payments",
    body: [
      "Paid plans are billed for the period selected at checkout. Invoices are available from your account. Prices are shown inclusive of applicable taxes unless stated otherwise.",
      "Unless otherwise required by law, fees for a period already started are non-refundable. Refunds, where applicable, are governed by our Refund Policy.",
      "You are responsible for maintaining valid payment details and for any applicable taxes associated with your use of paid features, including GST where it applies.",
    ],
  },
  {
    id: "use",
    title: "7. Acceptable use",
    body: [
      "You must not scrape, copy, resell or systematically extract the directory's data without our prior written permission.",
      "You must not attempt to disrupt, overload or gain unauthorised access to the service, or interfere with other users. You must not use the service for any unlawful purpose or in a manner that violates applicable Indian law.",
    ],
  },
  {
    id: "ip",
    title: "8. Intellectual property",
    body: [
      "The website, its design, software, compilation of listings, logos and trademarks are owned by or licensed to us and are protected under the Copyright Act, 1957 and the Trade Marks Act, 1999.",
      "You retain ownership of content you submit, such as your business logo and photographs, and you grant us a non-exclusive, royalty-free licence to host, display and reproduce that content for the purpose of operating the directory.",
    ],
  },
  {
    id: "liability",
    title: "9. Disclaimer and limitation of liability",
    body: [
      "The directory is provided on an 'as is' and 'as available' basis. We do not warrant that listings are complete, current or error-free, and we do not verify every claim made by third parties.",
      "We are a directory and not a party to any transaction between a visitor and a business. To the maximum extent permitted by law, we are not liable for any indirect or consequential loss arising from your use of the service or reliance on any listing.",
      "Nothing in these Terms limits any liability that cannot be excluded under applicable law, including under the Consumer Protection Act, 2019.",
    ],
  },
  {
    id: "termination",
    title: "10. Suspension and termination",
    body: [
      "We may suspend or terminate your access to the service if you breach these Terms, if required by law, or if we discontinue the service. On termination, your right to use the service ceases immediately.",
    ],
  },
  {
    id: "changes",
    title: "11. Changes to these terms",
    body: [
      "We may update these Terms from time to time. Material changes will be reflected on this page with an updated revision date. Continued use of the service after changes take effect constitutes acceptance of the revised Terms.",
    ],
  },
  {
    id: "law",
    title: "12. Governing law and jurisdiction",
    body: [
      "These Terms are governed by the laws of India. Subject to applicable law, any dispute arising out of or in connection with these Terms is subject to the exclusive jurisdiction of the competent courts at Mysore, Karnataka, India.",
    ],
  },
];

export default function TermsOfService() {
  return (
    <LegalDocument
      crumb="Terms of Service"
      eyebrow="Legal"
      icon="ri-file-list-3-line"
      heading="Terms of Service"
      subtitle={`The rules for using ${SITE.name}, listing your business, purchasing plans and interacting with our services, under the laws of India.`}
      updated="October 2026"
      seoTitle="Terms of Service"
      seoDescription={`Read the Terms of Service governing the use of ${SITE.name}, including listings, reviews, payments, acceptable use and Mysore jurisdiction.`}
      seoKeywords="Mysore Yellow Pages terms of service, directory terms, listing terms Karnataka"
      path="/terms-of-service"
      sections={sections}
    />
  );
}